import { useEffect, useState, useCallback } from "react";
import { Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { adminApi, errMsg, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", message: "", level: "info", target: "all", send_email: false };
const LEVELS = { info: "Informativo", warning: "Importante", success: "Novidade" };

export function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = useCallback(() => adminApi.get("/announcements").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); adminApi.get("/tenants", { params: { limit: 100 } }).then((r) => setTenants(r.data.items)).catch(() => {}); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/announcements", { title: f.title, message: f.message, level: f.level, send_email: f.send_email, tenant_ids: f.target === "all" ? [] : [f.target] });
      toast.success(f.send_email ? "Aviso publicado. Os e-mails estão sendo enviados." : "Aviso publicado");
      setF(EMPTY); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (a) => {
    if (!window.confirm("Remover este aviso do painel dos clientes?")) return;
    await adminApi.delete(`/announcements/${a.id}`); load();
  };
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <form onSubmit={send} className="card-surface p-5 space-y-3 lg:col-span-2" data-testid="announcement-form">
        <Field label="Título"><Input value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} required data-testid="announcement-title-input" /></Field>
        <Field label="Mensagem"><Textarea rows={5} value={f.message} onChange={(e) => setF((x) => ({ ...x, message: e.target.value }))} required data-testid="announcement-message-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo">
            <Select value={f.level} onValueChange={(v) => setF((x) => ({ ...x, level: v }))}>
              <SelectTrigger data-testid="announcement-level-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Destinatários">
            <Select value={f.target} onValueChange={(v) => setF((x) => ({ ...x, target: v }))}>
              <SelectTrigger data-testid="announcement-target-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos os clientes</SelectItem>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome_fantasia}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.send_email} onCheckedChange={(v) => setF((x) => ({ ...x, send_email: v }))} data-testid="announcement-email-switch" />Também enviar por e-mail</label>
        <Button type="submit" className="w-full h-11" data-testid="announcement-send-btn"><Send className="h-4 w-4 mr-2" />Publicar aviso</Button>
      </form>
      <div className="lg:col-span-3 space-y-2" data-testid="announcements-list">
        {items.map((a) => (
          <div key={a.id} className={`card-surface p-4 flex gap-3 ${a.active ? "" : "opacity-50"}`} data-testid={`admin-announcement-${a.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{a.title} <span className="text-xs font-normal text-muted-foreground">· {LEVELS[a.level]}{a.send_email ? " · e-mail" : ""}{a.active ? "" : " · removido"}</span></p>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{a.message}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtDateTime(a.created_at)} · {a.tenant_ids.length ? `${a.tenant_ids.length} cliente(s)` : "todos os clientes"} · dispensado por {a.dismissed} usuário(s)</p>
            </div>
            {a.active && <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`remove-announcement-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
        {!items.length && <p className="text-sm text-muted-foreground p-4">Nenhum aviso enviado.</p>}
      </div>
    </div>
  );
}
